import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

interface SingleSelectProps<T extends string | number | undefined> {
  options: T[];
  value: T;
  onChange: (value: T) => void;
  optionLabel?: (option: T) => string;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
}

const SingleSelect = <T extends string | number | undefined>({
  options,
  value,
  onChange,
  optionLabel = (option: T) => option?.toString() || '',
  placeholder = 'Select',
  disabled = false,
  isLoading = false,
  loadingText = 'Loading...',
  emptyText = 'No options available',
}: SingleSelectProps<T>) => {
  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            className={`relative w-full cursor-default rounded-lg border bg-white py-2 pr-10 pl-3 text-left shadow-xs disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 ${
              open ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
            }`}
          >
            <span className="block truncate">
              {value ? optionLabel(value) : placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-50 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
              {isLoading ? (
                <div className="px-4 py-2 text-gray-500">{loadingText}</div>
              ) : options.length === 0 ? (
                <div className="px-4 py-2 text-gray-500">{emptyText}</div>
              ) : (
                options.map((option) => (
                  <Listbox.Option
                    key={option}
                    className={({ active }) =>
                      `relative cursor-default py-2 pr-4 pl-10 select-none ${
                        active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      }`
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {optionLabel(option)}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <FontAwesomeIcon icon={faCheck} />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))
              )}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

export default SingleSelect;
